import { DeleteFilled, EditFilled } from "@ant-design/icons";
import { Col, Form, message, Modal, Row, Select, Spin, Tooltip } from "antd";
import { CALL_API } from "common/API";
import { APPROVAL_FILTER_TYPES, STATUS_CODE } from "common/Constants";
import { includes } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import {
  getClientid,
  getLeaveCategoryList,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import LeaveEncashmentForm from "organisms/LeaveEncashmentForm";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import {
  DateFilterForm,
  ENCASHMENT_STATUS,
  ENCASHMENT_STATUS_LIST,
  EncashmentStatusTag,
  formatAmount,
  formatDate,
  formatDays,
  getDateFilterRange,
  getStatusLabel,
  ProcessingStatusTag,
  StyledFilterRow,
  StyledTable,
} from "./common";
import LeaveEncashmentRequestModal from "./LeaveEncashmentRequestModal";

function MyLeaveEncashment({ ClientId, userInfo, userList, leaveCategoryList }) {
  const [dateForm] = Form.useForm();
  const [encashmentList, setEncashmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusId, setStatusId] = useState(ENCASHMENT_STATUS.WAITING);
  const [leaveType, setLeaveType] = useState("");
  const [selectedEncashment, setSelectedEncashment] = useState(null);
  const [editingEncashment, setEditingEncashment] = useState(null);
  const [CSVData, setCSVData] = useState([]);
  const csvLinkEl = useRef();
  const containerRef = useRef(null);

  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);

  // Same location / gender filtering as the leave forms.
  const userCategories = (leaveCategoryList || []).filter((item) => {
    if (!item?.locationId?.includes(finduser?.baselocationid)) return false;
    const eligible = item?.eligible?.toLowerCase();
    return finduser?.gender?.toLowerCase() === "male"
      ? eligible !== "female"
      : eligible !== "male";
  });

  const getEncashmentList = async () => {
    setLoading(true);
    const { code, encashments = [] } = await CALL_API(
      `leave-encashment-list/${ClientId}`,
      "post",
      {
        userid: userInfo?.userid,
        status_id: statusId,
        ...getDateFilterRange(dateForm),
      }
    );
    setLoading(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      setEncashmentList(encashments);
    } else {
      setEncashmentList([]);
    }
  };

  useEffect(() => {
    if (ClientId) getEncashmentList();
  }, [ClientId, statusId]);

  const filteredList = useMemo(
    () =>
      encashmentList
        .filter((item) => item?.userid === userInfo?.userid)
        .filter((item) => !statusId || item?.status_id === statusId)
        .filter(
          (item) =>
            !leaveType || String(item?.leave_category_id) === String(leaveType)
        )
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [encashmentList, statusId, leaveType, userInfo]
  );

  const deleteEncashment = (record) => {
    Modal.confirm({
      icon: "",
      title: "Delete leave encashment request",
      content: `Are you sure you want to delete your request to encash ${record?.number_of_days} day(s) of ${record?.leave_category_name}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        const { code } = await CALL_API(`save-leave-encashment/${ClientId}`, "post", {
          encashment_id: record?.encashment_id,
          is_delete: true,
        });
        if (code === STATUS_CODE.SUCCESS) {
          message.success("Leave encashment request has been deleted");
        } else if (code === STATUS_CODE.RECORD_EXIST) {
          message.error("This request has already been approved or rejected");
        } else if (code === STATUS_CODE.RECORD_NOT_FOUND) {
          message.error("This leave encashment request no longer exists");
        } else {
          message.error("Oops!! something went wrong");
        }
        getEncashmentList();
      },
    });
  };

  // Edit / delete are only possible while the request is waiting for approval.
  const actionColumn = {
    title: "Action",
    key: "action",
    render: (_, record) =>
      record?.status_id === ENCASHMENT_STATUS.WAITING ? (
        <span onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <EditFilled
              className="action-button"
              onClick={() => setEditingEncashment(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteFilled
              className="action-button"
              onClick={() => deleteEncashment(record)}
            />
          </Tooltip>
        </span>
      ) : (
        "-"
      ),
  };

  const columns = [
    { title: "Leave Type", dataIndex: "leave_category_name", key: "leave_category_name" },
    {
      title: "Submitted",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDate,
    },
    {
      title: "Days Requested",
      dataIndex: "number_of_days",
      key: "number_of_days",
      render: formatDays,
    },
    {
      title: "Status",
      dataIndex: "status_id",
      key: "status_id",
      render: (status) => <EncashmentStatusTag statusId={status} />,
    },
  ];
  if (statusId === ENCASHMENT_STATUS.APPROVED) {
    columns.push(
      {
        title: "Days Approved",
        dataIndex: "approved_days",
        key: "approved_days",
        render: formatDays,
      },
      {
        title: "Approved by",
        dataIndex: "approved_rejected_by_name",
        key: "approved_rejected_by_name",
        render: (name) => name || "-",
      },
      {
        title: "Amount Paid",
        dataIndex: "amount_paid",
        key: "amount_paid",
        render: (amount, record) => formatAmount(amount, record?.currency),
      },
      {
        title: "Payroll Period",
        dataIndex: "payroll_period",
        key: "payroll_period",
        render: (period) => period || "-",
      },
      {
        title: "Processing Status",
        dataIndex: "processing_status",
        key: "processing_status",
        render: (status) => <ProcessingStatusTag status={status || "Pending"} />,
      }
    );
  }
  if (statusId === ENCASHMENT_STATUS.REJECTED) {
    columns.push(
      {
        title: "Rejected by",
        dataIndex: "approved_rejected_by_name",
        key: "approved_rejected_by_name",
        render: (name) => name || "-",
      },
      {
        title: "Decline Reason",
        dataIndex: "decline_reason",
        key: "decline_reason",
        render: (reason) => reason || "-",
      }
    );
  } else {
    columns.push({
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason) => reason || "-",
    });
  }

  if (statusId === ENCASHMENT_STATUS.WAITING) columns.push(actionColumn);

  const prepareCSV = () => {
    if (!filteredList.length) {
      message.error("No data available to download.");
      return;
    }
    setCSVData(
      filteredList.map((item) => ({
        "Leave Type": item?.leave_category_name,
        Submitted: formatDate(item?.created_at),
        "Days Requested": item?.number_of_days,
        "Days Approved": item?.approved_days ?? "",
        Status: getStatusLabel(item?.status_id),
        "Approved/Rejected by": item?.approved_rejected_by_name || "",
        "Amount Paid": item?.amount_paid ?? "",
        "Payroll Period": item?.payroll_period || "",
        "Processing Status": item?.processing_status || "",
        Reason: item?.reason || "",
        "Decline Reason": item?.decline_reason || "",
      }))
    );
    setTimeout(() => csvLinkEl.current.link.click());
  };

  return (
    <>
      <Row className="page-title" ref={containerRef}>
        <Col xs={24} lg={20}>
          <StyledFilterRow>
            <Select
              value={statusId}
              style={{ width: 180 }}
              options={ENCASHMENT_STATUS_LIST}
              onChange={setStatusId}
              getPopupContainer={() => containerRef.current}
            />
            <DateFilterForm
              form={dateForm}
              name="my_leave_encashment_date_form"
              durationOptions={APPROVAL_FILTER_TYPES}
              initialDurationType={4}
              onChange={getEncashmentList}
              containerRef={containerRef}
            />
            <Select
              value={leaveType}
              showSearch
              style={{ width: 180 }}
              optionFilterProp="label"
              options={[
                { value: "", label: "All Categories" },
                ...userCategories.map((category) => ({
                  value: category?.leave_category_id,
                  label: category?.leave_category_name,
                })),
              ]}
              onChange={setLeaveType}
              getPopupContainer={() => containerRef.current}
            />
          </StyledFilterRow>
        </Col>
        <Col xs={24} lg={4} className="align-right">
          <ButtonOutlined onClick={prepareCSV}>Export CSV</ButtonOutlined>
          <CSVLink
            data={CSVData}
            filename={`my-leave-encashment-${getStatusLabel(statusId)}.csv`}
            ref={csvLinkEl}
          />
        </Col>
      </Row>
      <Spin spinning={loading}>
        <StyledTable
          dataSource={filteredList}
          columns={columns}
          rowKey="encashment_id"
          rowClassName={(_, index) =>
            `clickable-row ${index % 2 === 0 ? "" : "non-white"}`
          }
          onRow={(record) => ({ onClick: () => setSelectedEncashment(record) })}
          pagination={filteredList.length > 100 ? { pageSize: 100 } : false}
        />
      </Spin>
      <LeaveEncashmentForm
        isModalVisible={!!editingEncashment}
        encashment={editingEncashment}
        closeModal={(saved) => {
          setEditingEncashment(null);
          if (saved) getEncashmentList();
        }}
      />
      <LeaveEncashmentRequestModal
        isModalVisible={!!selectedEncashment}
        closeModal={() => setSelectedEncashment(null)}
        encashment={selectedEncashment}
        ClientId={ClientId}
        canApprove={false}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  ClientId: getClientid(state),
  userInfo: getUserInfo(state),
  userList: getUserList(state),
  leaveCategoryList: getLeaveCategoryList(state),
});

export default connect(mapStateToProps, null)(MyLeaveEncashment);
